import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { clsx } from 'clsx';

import { authActions } from '@ott/solar-auth';
import Footer from '@ott/solar-footer';
import sendKM from '@ott/utility-km';

import { getAuthSelector } from 'src/redux/selectors/commonSelectors';

import useIsWebview from 'src/hooks/useIsWebview';
import useTypedSelector from 'src/hooks/useTypedSelector';

import { ReportsForm } from './blocks/ReportsForm';
import { ReportsHeader } from './blocks/ReportsHeader';
import * as ReportsStage from './blocks/ReportsStage';
import { REPORTS_BG } from './constants';

import styles from './ReportsPage.scss';

const PRIVACY_POLICY_LINK =
  'https://support.solartrip.com/hc/en-gb/articles/4768495535133-Privacy-Policy';

const ReportsPage = () => {
  const isWebview = useIsWebview();

  const auth = useTypedSelector(getAuthSelector);
  const dispatch = useDispatch();

  useEffect(() => {
    sendKM('SOLAR_REPORTS_PAGE_SHOW');

    if (auth.infoStatus.initial) {
      dispatch(authActions.getBasicUserInfo());
    }
  }, []);

  if (typeof window === 'undefined') {
    return null;
  }

  return (
    <>
      <ReportsHeader />

      <div className={styles.bgWrapper}>
        <img className={styles.bg} src={REPORTS_BG} alt="Coin" sizes="1920px" />
      </div>

      <main className={styles.page}>
        <div className={styles.wrapper}>
          <section className={styles.intro}>
            <div className={styles.introContent}>
              <h1 className={styles.headingH1}>
                The Loyalty Revolution
                <span className={clsx(styles.headingH1Tiny, styles.introHeadingTiny)}>Report</span>
              </h1>

              <p className={styles.pNormal}>
                With extensive industry experience, Solartrip has researched emerging travel trends
                and evolving customer loyalty, all&nbsp;curated into one essential report for you
                to&nbsp;download.
              </p>
            </div>

            <div className={clsx(styles.formWrapper, styles.formWrapperMobile)}>
              <b className={styles.formTitle}>
                Discover how travel rewards can help you grow loyalty, attract members,
                and&nbsp;drive&nbsp;real value.
              </b>
              <ReportsForm />
              <p className={styles.formDescription}>
                We&rsquo;ll email you a&nbsp;link to&nbsp;download Solartrip&rsquo;s Loyalty
                Revolution Report. By&nbsp;clicking &laquo;Get the Report&raquo;, you agree
                to&nbsp;the{' '}
                <a className={styles.link} href={PRIVACY_POLICY_LINK} target="_blank">
                  Privacy Policy
                </a>
              </p>
            </div>
          </section>

          <section>
            <h2 className={styles.headingH1}>
              <span className={clsx(styles.headingH1Tiny, styles.loyaltyTiny)}>
                Travel loyalty is&nbsp;no&nbsp;longer just about points&nbsp;&mdash; it&rsquo;s
                about
              </span>
              experiences, personalization, and smart tech.
            </h2>
          </section>

          <section className={styles.stages}>
            <h2 className={styles.headingH1}>
              <span className={styles.headingH1Tiny}>Our</span> latest{' '}
              <span className={styles.headingH1Tiny}>report&nbsp;reveals</span>
            </h2>

            <ReportsStage.List>
              <ReportsStage.ListItem>
                <ReportsStage.Check />

                <div>
                  <ReportsStage.Text>Why Gen Z and Millennials</ReportsStage.Text>
                  <ReportsStage.Text>value experiences over cashback</ReportsStage.Text>
                </div>
              </ReportsStage.ListItem>

              <ReportsStage.ListItem>
                <ReportsStage.Check />

                <div>
                  <ReportsStage.Text>How embedded travel rewards</ReportsStage.Text>
                  <ReportsStage.Text>drive engagement and retention</ReportsStage.Text>
                </div>
              </ReportsStage.ListItem>

              <ReportsStage.ListItem>
                <ReportsStage.Check />

                <div>
                  <ReportsStage.Text>
                    The role of&nbsp;AI, crypto, and white-label
                  </ReportsStage.Text>
                  <ReportsStage.Text>platforms in&nbsp;next-gen loyalty</ReportsStage.Text>
                </div>
              </ReportsStage.ListItem>
            </ReportsStage.List>
          </section>
        </div>

        <section className={clsx(styles.formWrapper, styles.formWrapperDesktop)}>
          <b className={styles.formTitle}>
            Discover how travel rewards can help you grow loyalty, attract members,
            and&nbsp;drive&nbsp;real value.
          </b>
          <ReportsForm />
          <p className={styles.formDescription}>
            We&rsquo;ll email you a&nbsp;link to&nbsp;download Solartrip&rsquo;s Loyalty Revolution
            Report. By&nbsp;clicking &laquo;Get the Report&raquo;, you agree to&nbsp;the{' '}
            <a className={styles.link} href={PRIVACY_POLICY_LINK} target="_blank">
              Privacy Policy
            </a>
          </p>
        </section>
      </main>

      {!isWebview && <Footer />}
    </>
  );
};

export default ReportsPage;
